import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { HiUserGroup } from "react-icons/hi";
import { MdManageAccounts } from "react-icons/md";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import arrow from "../../../images/Sidebar/arrow.svg";

// Data for navigation links
const sidebarLinks = [
  { to: "/corporate/hr", icon: <MdManageAccounts />, label: "Admin" },
];

// Reusable NavLink component
const SidebarNavLink = ({ to, icon, label, collapsed }) => (
  <li>
    <NavLink
      to={to}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-3 py-2 font-Roboto text-base rounded-lg transition-all duration-300 ${
          isActive
            ? collapsed
              ? "bg-[#FCDD00] text-black rounded-full w-[50px] h-[50px] flex items-center justify-center"
              : "bg-[#FCDD00] text-black"
            : collapsed
            ? "text-[#1A1A1A] rounded-full w-[50px] h-[50px] flex items-center justify-center hover:bg-gray-100"
            : "text-[#1A1A1A] hover:bg-gray-100"
        }`
      }
    >
      <span className="text-xl">{icon}</span>
      {!collapsed && label}
      {collapsed && (
        <span className="fixed left-[80px] rounded-md bg-black text-white text-sm px-2 py-1 opacity-0 group-hover:opacity-100 whitespace-nowrap z-[9999] pointer-events-none">
          <div className="absolute top-1/2 translate-y-[-50%] -left-1 w-2 h-2 bg-black transform rotate-45"></div>
          {label}
        </span>
      )}
    </NavLink>
  </li>
);

const CorporateSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const [openmenu, setOpenMenu] = useState(false);

  // Floating menu states
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);

  const isMemberActive = [
    "/corporate/uploademployee",
    "/corporate/employee",
  ].some((subPath) => location.pathname.startsWith(subPath));

  useEffect(() => {
    setOpenMenu(isMemberActive);
  }, [isMemberActive, location.pathname]);

  const user = localStorage.getItem("Admin");
  const newcorporate = JSON.parse(user);

  const handleMouseEnter = () => {
    if (collapsed && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.top, left: 80 });
      setShowFloatingMenu(true);
    }
  };

  const handleMouseLeave = () => {
    if (collapsed) {
      setShowFloatingMenu(false);
    }
  };

  return (
    <div
      className={`flex flex-col transition-all duration-300 bg-white ${
        collapsed
          ? "w-[80px] overflow-hidden justify-center"
          : "w-[200px] justify-between"
      }`}
    >
      <div className="min-w-[200px] bg-white flex-col justify-between flex">
        {/* Collapse Button */}
        <div className="relative flex flex-col justify-center gap-2 px-3 py-3 text-xl group">
          <button
            onClick={() => setCollapsed((prevState) => !prevState)}
            className="text-[#1A1A1A] rounded-lg w-[50px] h-[50px] flex items-center justify-center hover:bg-gray-100"
          >
            {collapsed ? (
              <PanelRightOpen className="w-[20px] h-[20px]" />
            ) : (
              <PanelRightClose className="w-[20px] h-[20px]" />
            )}
          </button>
          {/* Tooltip */}
    <span
            className="fixed left-[80px] rounded-md bg-black text-white text-sm px-2 py-1
                      opacity-0 group-hover:opacity-100 whitespace-nowrap z-[9999] pointer-events-none"
          >
            <div className="absolute top-1/2 translate-y-[-50%] -left-1 w-2 h-2 bg-black transform rotate-45"></div>
    {collapsed ? "Show" : "Hide"}
        </span>
        </div>

        {/* Sidebar Links */}
        <div>
          <ul className="px-3 py-5 flex flex-col gap-2 min-w-[200px]">
            {sidebarLinks.map((link) => (
              <SidebarNavLink
                key={link.to}
                to={link.to}
                icon={link.icon}
                label={link.label}
                collapsed={collapsed}
              />
            ))}

            {/* Member with floating submenu */}
            <li
              ref={menuRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div
                onClick={() => {
                  setOpenMenu(!openmenu);
                  if (collapsed) setCollapsed(false);
                }}
                className={`relative group flex items-center px-3 py-2 cursor-pointer rounded-lg ${
                  collapsed ? "w-[50px] h-[50px] justify-center" : "justify-between"
                } ${
                  isMemberActive && collapsed
                    ? "bg-[#FCDD00] text-black"
                    : "text-[#1A1A1A] hover:bg-gray-100"
                }`}
              >
                <div className={`flex gap-3 items-center ${collapsed ? "my-1" : ""}`}>
                  <span>
                    <HiUserGroup />
                  </span>
                  {!collapsed && (
                    <span className="text-base font-Roboto">Member</span>
                  )}
                  {/* hide tooltip when floating submenu is showing */}
                  {collapsed && !showFloatingMenu && (
                    <span className="fixed left-[80px] rounded-md bg-black text-white text-sm px-2 py-1 opacity-0 group-hover:opacity-100 whitespace-nowrap z-[9999] pointer-events-none">
                      <div className="absolute top-1/2 translate-y-[-50%] -left-1 w-2 h-2 bg-black transform rotate-45"></div>
                      Member
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <img
                    src={arrow}
                    alt="arrow"
                    className={`${openmenu ? "rotate-180" : ""}`}
                  />
                )}
              </div>

              {/* Expanded submenu when sidebar is open */}
              {!collapsed && openmenu && (
                <div className="pl-[22px]">
                  {newcorporate?.data?.name !== "Vibhuti" && (
                    <NavLink
                      to="/corporate/uploademployee"
                      className={({ isActive }) =>
                        `flex items-center font-Roboto text-base py-2 px-3 ${
                          isActive
                            ? "bg-[#FCDD00] text-black rounded-lg p-3"
                            : "text-[#1A1A1A]"
                        }`
                      }
                    >
                      Create Member
                    </NavLink>
                  )}
                  <NavLink
                    to="/corporate/employee"
                    className={({ isActive }) =>
                      `flex items-center font-Roboto text-base py-2 px-3 ${
                        isActive
                          ? "bg-[#FCDD00] text-black rounded-lg p-3"
                          : "text-[#1A1A1A]"
                      }`
                    }
                  >
                    Member List
                  </NavLink>
                </div>
              )}

              {/* Floating submenu when collapsed */}
              {collapsed && showFloatingMenu && (
                <div
                  className="fixed z-20 py-2 bg-white rounded-md shadow-lg w-44"
                  style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
                >
                  {newcorporate?.data?.name !== "Vibhuti" && (
                    <NavLink
                      to="/corporate/uploademployee"
                      className={({ isActive }) =>
                        `block px-3 py-2 font-Roboto text-base ${
                          isActive
                            ? "bg-[#FCDD00] text-black"
                            : "text-[#1A1A1A] hover:bg-gray-100"
                        }`
                      }
                    >
                      Create Member
                    </NavLink>
                  )}
                  <NavLink
                    to="/corporate/employee"
                    className={({ isActive }) =>
                      `block px-3 py-2 font-Roboto text-base ${
                        isActive
                          ? "bg-[#FCDD00] text-black"
                          : "text-[#1A1A1A] hover:bg-gray-100"
                      }`
                    }
                  >
                    Member List
                  </NavLink>
                </div>
              )}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CorporateSidebar;
